import React, { useState, useEffect, useCallback } from 'react';
import { X, Clock, Copy, Check, Trash2, ExternalLink } from 'lucide-react';
import apostilaPreviewService, {
  PreviewDuration,
  ActivePreviewToken,
} from '../../services/apostilaPreview.service';
import './RyanFlashcardPreviewModal.css';

interface SubdeckData {
  _id: string;
  name: string;
  deckId: string;
  pageCount?: number;
}

function formatTimeLeft(expiresAt: string | null): string {
  if (!expiresAt) return 'Sempre disponível';
  const diffMs = new Date(expiresAt).getTime() - Date.now();
  if (diffMs <= 0) return 'Expirado';
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  return diffH > 0 ? `${diffH}h ${diffMin % 60}min restantes` : `${diffMin}min restantes`;
}

const DURATION_SHORT: Record<PreviewDuration, string> = {
  '15min': '15min',
  '30min': '30min',
  '60min': '1h',
  '5h': '5h',
  '12h': '12h',
  '24h': '24h',
  'always': 'Sempre',
};

const RyanApostilaPreviewModal: React.FC<{
  spaceId: string;
  subdeck: SubdeckData;
  onClose: () => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}> = ({ spaceId, subdeck, onClose, onToast }) => {
  const [duration, setDuration] = useState<PreviewDuration>('always');
  const [pageLimit, setPageLimit] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeTokens, setActiveTokens] = useState<ActivePreviewToken[]>([]);
  const [tokensLoading, setTokensLoading] = useState(true);

  const token = localStorage.getItem('accessToken') || '';

  const loadTokens = useCallback(async () => {
    setTokensLoading(true);
    try {
      const tokens = await apostilaPreviewService.listActiveTokens(subdeck.deckId, subdeck._id, token);
      setActiveTokens(tokens);
    } catch {
      // non-critical
    } finally {
      setTokensLoading(false);
    }
  }, [subdeck.deckId, subdeck._id, token]);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const limit = pageLimit && parseInt(pageLimit) > 0 ? parseInt(pageLimit) : null;
      const result = await apostilaPreviewService.generateToken(
        subdeck.deckId, subdeck._id, duration, limit, token
      );
      const url = `${window.location.origin}/previa/${result.urlSlug}`;
      setGeneratedUrl(url);
      loadTokens();
    } catch (err: any) {
      onToast(err?.response?.data?.message || err.message || 'Erro ao gerar prévia', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (urlSlug: string) => {
    try {
      await apostilaPreviewService.revokeToken(urlSlug, token);
      setActiveTokens(prev => prev.filter(t => t.urlSlug !== urlSlug));
      onToast('Prévia revogada.', 'success');
    } catch {
      onToast('Erro ao revogar prévia', 'error');
    }
  };

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rfpm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rfpm-modal">
        <div className="rfpm-header">
          <div>
            <h3 className="rfpm-title">Configurar prévia da apostila</h3>
            <p className="rfpm-subtitle">{subdeck.name}</p>
          </div>
          <button className="rfpm-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="rfpm-body">
          {/* Duração */}
          <div className="rfpm-section">
            <div className="rfpm-section-label">
              <Clock size={13} style={{ display: 'inline', marginRight: 5 }} />
              Duração do link
            </div>
            <div className="rfpm-duration-row">
              {(Object.keys(DURATION_SHORT) as PreviewDuration[]).map(d => (
                <button
                  key={d}
                  className={`rfpm-duration-btn${duration === d ? ' active' : ''}`}
                  onClick={() => setDuration(d)}
                >
                  {DURATION_SHORT[d]}
                </button>
              ))}
            </div>
          </div>

          {/* Limite de páginas */}
          {subdeck.pageCount && subdeck.pageCount > 0 && (
            <div className="rfpm-section">
              <div className="rfpm-section-label">
                Limite de páginas <span className="rfpm-optional">(opcional)</span>
              </div>
              <input
                type="number"
                min={1}
                max={subdeck.pageCount}
                placeholder={`Sem limite (total: ${subdeck.pageCount} págs.)`}
                className="rfpm-input"
                value={pageLimit}
                onChange={e => setPageLimit(e.target.value)}
              />
              <p className="rfpm-hint">Número de páginas visíveis na prévia.</p>
            </div>
          )}

          {/* Botão gerar */}
          {!generatedUrl ? (
            <button
              className="rfpm-generate-btn"
              onClick={handleGenerate}
              disabled={generating}
            >
              {generating ? 'Gerando...' : 'Gerar link de prévia'}
            </button>
          ) : (
            <div className="rfpm-generated">
              <p className="rfpm-generated-msg">
                Link gerado! Duração: <strong>{DURATION_SHORT[duration]}</strong>
                {pageLimit && parseInt(pageLimit) > 0 ? ` · ${pageLimit} págs.` : ''}
              </p>
              <div className="rfpm-url-row">
                <input
                  className="rfpm-url-input"
                  readOnly
                  value={generatedUrl}
                  onClick={e => (e.target as HTMLInputElement).select()}
                />
                <button
                  className={`rfpm-copy-btn${copied ? ' copied' : ''}`}
                  onClick={() => handleCopy(generatedUrl!)}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
              <div className="rfpm-generated-actions">
                <button className="rfpm-link-btn" onClick={() => setGeneratedUrl(null)}>
                  Gerar novo link
                </button>
                <a className="rfpm-link-btn rfpm-open-link" href={generatedUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={12} /> Abrir prévia
                </a>
              </div>
            </div>
          )}

          {/* Links ativos */}
          {(tokensLoading || activeTokens.length > 0) && (
            <div className="rfpm-section rfpm-section--active">
              <div className="rfpm-section-label">Links ativos</div>
              {tokensLoading ? (
                <div className="rfpm-loading">Carregando...</div>
              ) : (
                <div className="rfpm-active-list">
                  {activeTokens.map(t => (
                    <div key={t._id} className="rfpm-active-item">
                      <div className="rfpm-active-info">
                        <span className="rfpm-active-slug">/{t.urlSlug}</span>
                        <span className="rfpm-active-meta">
                          <Clock size={10} />
                          {formatTimeLeft(t.expiresAt)}
                          {t.previewPagesLimit && ` · máx ${t.previewPagesLimit} págs.`}
                        </span>
                      </div>
                      <div className="rfpm-active-btns">
                        <button
                          className="rfpm-active-copy"
                          title="Copiar link"
                          onClick={() => handleCopy(`${window.location.origin}/previa/${t.urlSlug}`)}
                        >
                          <Copy size={12} />
                        </button>
                        <button
                          className="rfpm-active-revoke"
                          title="Revogar"
                          onClick={() => handleRevoke(t.urlSlug)}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RyanApostilaPreviewModal;
